// Calendar-only helpers for the administrative users screen. Payment due dates
// arrive as YYYY-MM-DD, so never parse them as instants (which can move a date
// around at UTC offsets).
export const DUE_SOON_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

const calendarDayMs = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const result = Date.UTC(year, month - 1, day);
  const verified = new Date(result);
  return verified.getUTCFullYear() === year && verified.getUTCMonth() === month - 1 && verified.getUTCDate() === day
    ? result
    : null;
};

const todayMs = (now = new Date()) => Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

export const getDueDateDetails = (dueDate, now = new Date()) => {
  const dueMs = calendarDayMs(dueDate);
  if (dueMs === null) return { state: 'none', days: null, hint: 'Sin vencimiento' };
  const days = Math.round((dueMs - todayMs(now)) / DAY_MS);
  if (days < 0) return { state: 'overdue', days, hint: `Vencido hace ${Math.abs(days)} día${Math.abs(days) === 1 ? '' : 's'}` };
  if (days === 0) return { state: 'soon', days, hint: 'Vence hoy' };
  if (days === 1) return { state: 'soon', days, hint: 'Vence mañana' };
  if (days <= DUE_SOON_DAYS) return { state: 'soon', days, hint: `Vence en ${days} días` };
  return { state: 'ok', days, hint: `Vence en ${days} días` };
};

// Kept as a concise compatibility helper for existing callers and tests.
export const getPaymentState = (row, now = new Date()) => getDueDateDetails(row?.nextPaymentDueDate, now).state;

export const getAccountState = (row, now = new Date()) => {
  if (row?.isActive === false) return 'suspended';
  // An employee has no payment of its own: it follows its owner account (set by the dashboard).
  if (row?.ownerUserId) return row.ownerAccountState || 'active';
  if (row?.paymentBlockedAt) return 'blocked';
  const paymentState = getPaymentState(row, now);
  if (paymentState === 'overdue') return 'overdue';
  if (paymentState === 'soon') return 'soon';
  return 'active';
};

export const DEFAULT_USER_FILTERS = { search: '', status: 'ALL' };

export const filterUsers = (rows, filters = DEFAULT_USER_FILTERS, now = new Date()) => {
  const term = String(filters.search || '').trim().toLowerCase();
  const digits = term.replace(/[^0-9]/g, '');
  return (rows || []).filter((row) => {
    if (term) {
      const text = `${row.username || ''} ${row.email || ''}`.toLowerCase();
      const phone = String(row.phoneNumber || '').replace(/[^0-9]/g, '');
      if (!text.includes(term) && !(digits && phone.includes(digits))) return false;
    }
    // Compatibility for former filter consumers; the current admin UI uses the
    // explicit derived-state values below.
    if (filters.role && filters.role !== 'ALL' && row.role !== filters.role) return false;
    if (filters.status === 'ACTIVE' && row.isActive === false) return false;
    if (filters.status === 'INACTIVE' && row.isActive !== false) return false;
    if (filters.payment && filters.payment !== 'ALL' && getPaymentState(row, now) !== filters.payment) return false;
    const state = getAccountState(row, now);
    return filters.status === 'ALL' || filters.status === 'ACTIVE' || filters.status === 'INACTIVE' || state === filters.status;
  });
};

export const sortUsersByPriority = (rows, now = new Date()) => [...(rows || [])].sort((left, right) => {
  const rank = (row) => {
    const state = getPaymentState(row, now);
    if (state === 'overdue') return 0;
    if (state === 'soon') return 1;
    return 2;
  };
  const rankDifference = rank(left) - rank(right);
  if (rankDifference !== 0) return rankDifference;
  const leftDate = calendarDayMs(left?.nextPaymentDueDate) ?? Number.MAX_SAFE_INTEGER;
  const rightDate = calendarDayMs(right?.nextPaymentDueDate) ?? Number.MAX_SAFE_INTEGER;
  if (leftDate !== rightDate) return leftDate - rightDate;
  return String(left?.username || '').localeCompare(String(right?.username || ''));
});

export const formatDueDate = (value) => {
  const dateMs = calendarDayMs(value);
  if (dateMs === null) return '—';
  return new Intl.DateTimeFormat('es-AR', { timeZone: 'UTC' }).format(new Date(dateMs));
};
